import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from "../ui/input";
import { Drawer, DrawerTrigger, DrawerContent, DrawerClose } from '../ui/drawer';
import { updateProfile, Profile } from '../../features/profileSlice';
import { auth } from "../../firebase";
import { useTheme, ACCENT_COLORS } from '../../context/ThemeContext';

interface ProfileCardProps {
    user: Profile;
}

const ProfileCard: React.FC<ProfileCardProps> = ({ user }) => {
    const [editedUser, setEditedUser] = useState<Profile>(user);
    const queryClient = useQueryClient();
    const currentUser = auth.currentUser;
    const { accentColor, setAccentColor } = useTheme();

    // Apply saved accent color on mount
    useEffect(() => {
        if (user.accentColor) {
            setAccentColor(user.accentColor);
        }
    }, [user.accentColor, setAccentColor]);

    const updateProfileMutation = useMutation({
        mutationKey: ['editProfile'],
        mutationFn: (editedUser: Profile) => updateProfile(currentUser!.uid ?? '', editedUser),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['profile'] });
        },
    });

    const handleSaveClick = () => {
        updateProfileMutation.mutate(editedUser);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setEditedUser(prevUser => ({
            ...prevUser,
            [name]: value,
        }));
    };

    // Get initials for avatar
    const getInitials = (username: string) => {
        return username
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const handleAccentColorChange = (colorValue: string) => {
        setAccentColor(colorValue);
        const updatedUser = { ...editedUser, accentColor: colorValue };
        setEditedUser(updatedUser);
        updateProfileMutation.mutate(updatedUser);
    };

    return (
        <div className="flex flex-col pb-24">
            {/* Content */}
            <div className="px-5 pt-6 space-y-7">
                {/* Profile Header */}
                <div className="flex flex-col items-center gap-3">
                    <div className="w-20 h-20 rounded-full bg-tmw-accent flex items-center justify-center">
                        <span className="text-[28px] font-semibold text-tmw-accent-foreground">
                            {getInitials(user.username || 'U')}
                        </span>
                    </div>
                    <h2 className="text-[22px] font-bold text-foreground">{user.username}</h2>
                </div>

                {/* Stats Row */}
                <div className="flex gap-3">
                    <div className="flex-1 bg-tmw-surface-elevated rounded-xl p-4 flex flex-col items-center gap-1">
                        <span className="text-[13px] text-muted-foreground">Height</span>
                        <span className="text-xl font-semibold text-foreground">
                            {user.heightFeet}' {user.heightInches}"
                        </span>
                    </div>
                    <div className="flex-1 bg-tmw-surface-elevated rounded-xl p-4 flex flex-col items-center gap-1">
                        <span className="text-[13px] text-muted-foreground">Weight</span>
                        <span className="text-xl font-semibold text-foreground">
                            {user.weightPounds} lbs
                        </span>
                    </div>
                </div>

                {/* Workout Stats Section */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-foreground">Workout Stats</h3>

                    <div className="space-y-3">
                        {/* Top Row */}
                        <div className="flex gap-3">
                            <div className="flex-1 bg-tmw-surface-elevated rounded-xl p-4 flex flex-col gap-1">
                                <span className="text-[13px] text-muted-foreground">Total Workouts</span>
                                <span className="text-2xl font-bold text-tmw-accent">47</span>
                            </div>
                            <div className="flex-1 bg-tmw-surface-elevated rounded-xl p-4 flex flex-col gap-1">
                                <span className="text-[13px] text-muted-foreground">Streak</span>
                                <span className="text-2xl font-bold text-tmw-accent">12 days</span>
                            </div>
                        </div>

                        {/* Favorite Exercise */}
                        <div className="bg-tmw-surface-elevated rounded-xl p-4 flex flex-col gap-1">
                            <span className="text-[13px] text-muted-foreground">Favorite Exercise</span>
                            <span className="text-2xl font-bold text-foreground">Bench Press</span>
                        </div>
                    </div>
                </div>

                {/* Accent Color Section */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-foreground">Accent Color</h3>
                    <div className="grid grid-cols-4 gap-3">
                        {ACCENT_COLORS.map((color) => (
                            <button
                                key={color.name}
                                onClick={() => handleAccentColorChange(color.value)}
                                className="aspect-square rounded-xl flex items-center justify-center transition-transform active:scale-95"
                                style={{ backgroundColor: `hsl(${color.value})` }}
                            >
                                {accentColor === color.value && (
                                    <Check className="h-5 w-5 text-white" />
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Edit Profile Button */}
                <Drawer>
                    <DrawerTrigger asChild>
                        <Button variant="secondary" className="w-full">
                            Edit Profile
                        </Button>
                    </DrawerTrigger>
                    <DrawerContent className="p-4">
                        <h3 className="text-lg font-semibold text-foreground mb-4">Edit Profile</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="text-sm text-muted-foreground mb-1.5 block">Username</label>
                                <Input
                                    className="h-12"
                                    type="text"
                                    name="username"
                                    placeholder="Username"
                                    value={editedUser.username || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="flex gap-3">
                                <div className="flex-1">
                                    <label className="text-sm text-muted-foreground mb-1.5 block">Height (ft)</label>
                                    <Input
                                        className="h-12"
                                        type="text"
                                        name="heightFeet"
                                        placeholder="Feet"
                                        value={editedUser.heightFeet || ''}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="flex-1">
                                    <label className="text-sm text-muted-foreground mb-1.5 block">Height (in)</label>
                                    <Input
                                        className="h-12"
                                        type="text"
                                        name="heightInches"
                                        placeholder="Inches"
                                        value={editedUser.heightInches || ''}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-sm text-muted-foreground mb-1.5 block">Weight (lbs)</label>
                                <Input
                                    className="h-12"
                                    type="text"
                                    name="weightPounds"
                                    placeholder="Weight in pounds"
                                    value={editedUser.weightPounds || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                        <div className="mt-6">
                            <DrawerClose asChild>
                                <Button onClick={handleSaveClick} className="w-full">
                                    Save Changes
                                </Button>
                            </DrawerClose>
                        </div>
                    </DrawerContent>
                </Drawer>
            </div>
        </div>
    );
};

export default ProfileCard;
